import { useId, useState } from 'react'
import { FolderUsageList } from '@/components/FolderUsageList'
import { CollectedAtLine } from '@/components/SnapshotCard'
import { Badge } from '@/components/ui/badge'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { folderUsageFromDiskSnapshot } from '@/lib/folderUsage'
import { formatDateTime } from '@/lib/formatAgent'
import { diskSpaceStatus, resolveDiskFree } from '@/lib/hardwareTrafficLight'
import { DEFAULT_POSTURE_THRESHOLDS } from '@/lib/postureThresholds'

function SnapshotField({ label, value }) {
  const text = value == null || value === '' ? '—' : String(value)
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{text}</span>
    </div>
  )
}

function volumePillVariant(pct, freeGb, thresholds) {
  const status = diskSpaceStatus(pct, freeGb, thresholds)
  if (status === 'red') return 'destructive'
  if (status === 'amber') return 'warning'
  if (status === 'green') return 'success'
  return 'secondary'
}

export function DiskSpaceSnapshotCard({ snapshot, thresholds = DEFAULT_POSTURE_THRESHOLDS }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  if (!snapshot) return null
  const data = snapshot.payload || {}
  const volumes = Array.isArray(data.volumes) ? data.volumes : []
  const t = { ...DEFAULT_POSTURE_THRESHOLDS, ...(thresholds || {}) }
  const { lowestFreePct: lowest, lowestFreeGb } = resolveDiskFree(data, {})
  const spaceStatus = diskSpaceStatus(lowest, lowestFreeGb, t)
  const isCritical = spaceStatus === 'red'
  const isLow = spaceStatus === 'amber'
  const folderUsage = folderUsageFromDiskSnapshot(data)

  const summaryParts = []
  if (lowest != null) summaryParts.push(`${lowest}% free`)
  if (lowestFreeGb != null) summaryParts.push(`${Number(lowestFreeGb).toFixed(1)} GB free`)
  if (volumes.length > 0) summaryParts.push(`${volumes.length} volume(s)`)
  if (folderUsage?.hasFolders) summaryParts.push(`${folderUsage.folders.length} folders`)
  if (folderUsage?.hasUserProfiles) {
    summaryParts.push(`${folderUsage.userProfiles.length} user profile(s)`)
  }
  if (snapshot.collected_at) {
    summaryParts.push(`Collected ${formatDateTime(snapshot.collected_at)}`)
  }

  return (
    <CollapsePanel
      id={`disk-space-${id}`}
      title="Disk space"
      collapsedSummary={summaryParts.join(' · ') || undefined}
      open={open}
      onOpenChange={setOpen}
      pill={lowest != null ? `${lowest}% free` : undefined}
      pillVariant={
        spaceStatus == null
          ? 'inactive'
          : isCritical
            ? 'destructive'
            : isLow
              ? 'pending'
              : 'ready'
      }
      bodyClassName="space-y-2"
    >
      <CollectedAtLine collectedAt={snapshot.collected_at} />
      {data.system_drive && (
        <>
          <SnapshotField label="System drive" value={data.system_drive} />
          <SnapshotField
            label="System drive free"
            value={
              data.system_free_percent != null
                ? `${data.system_free_percent}% (${data.system_free_gb ?? '—'} GB free)`
                : null
            }
          />
        </>
      )}
      <div className="flex justify-between gap-4 text-sm">
        <span className="text-muted-foreground">Lowest free space</span>
        {lowest != null ? (
          <Badge
            variant={isCritical ? 'destructive' : isLow ? 'warning' : 'success'}
            title={`Low below ${t.disk_low_pct}% or ${t.disk_low_gb} GB; critical below ${t.disk_critical_pct}% or ${t.disk_critical_gb} GB`}
          >
            {lowest}%
            {lowestFreeGb != null ? ` · ${Number(lowestFreeGb).toFixed(1)} GB` : ''}
          </Badge>
        ) : (
          <span className="font-medium">—</span>
        )}
      </div>

      {volumes.length > 0 ? (
        <div className="pt-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">Volumes</p>
          <div className="rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Drive</th>
                  <th className="px-3 py-2 font-medium">Label</th>
                  <th className="px-3 py-2 font-medium text-right">Free</th>
                  <th className="px-3 py-2 font-medium text-right">Total</th>
                  <th className="px-3 py-2 font-medium text-right">Free %</th>
                </tr>
              </thead>
              <tbody>
                {volumes.map((vol, index) => {
                  const pct = vol.free_percent
                  return (
                    <tr
                      key={`${vol.drive}-${index}`}
                      className="border-b border-border last:border-0"
                    >
                      <td className="px-3 py-2 font-medium">{vol.drive || '—'}</td>
                      <td className="px-3 py-2">{vol.label || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {vol.free_gb != null ? `${vol.free_gb} GB` : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {vol.total_gb != null ? `${vol.total_gb} GB` : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {pct != null ? (
                          <Badge variant={volumePillVariant(pct, vol.free_gb, t)}>{pct}%</Badge>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground pt-1">No fixed disks reported.</p>
      )}

      {folderUsage?.hasFolders ? (
        <div className="pt-4 border-t border-border mt-4">
          <p className="text-xs font-medium text-muted-foreground mb-1">
            Largest folders on {folderUsage.drive || 'system drive'}
          </p>
          {folderUsage.used_gb != null && (
            <p className="text-xs text-muted-foreground mb-3">
              Top-level folders by size ({folderUsage.used_gb} GB used on this drive).
            </p>
          )}
          <FolderUsageList
            folders={folderUsage.folders}
            percentKey="percent_of_used"
            emptyMessage="No folder breakdown available yet."
          />
        </div>
      ) : folderUsage ? (
        <div className="pt-4 border-t border-border mt-4">
          <p className="text-xs font-medium text-muted-foreground mb-1">
            Largest folders on {folderUsage.drive || 'system drive'}
          </p>
          <p className="text-sm text-muted-foreground">
            {folderUsage.error
              ? `Folder scan failed on the endpoint: ${folderUsage.error}`
              : 'Folder breakdown was not included in the last snapshot. Re-run disk-space v1.2.1+ on this device and allow the C: scan to finish.'}
          </p>
        </div>
      ) : (
        <div className="pt-4 border-t border-border mt-4">
          <p className="text-xs font-medium text-muted-foreground mb-1">Largest folders</p>
          <p className="text-sm text-muted-foreground">
            Re-run the disk-space script (v1.2.1+) on this device to populate a TreeSize-style
            folder list.
          </p>
        </div>
      )}

      {folderUsage?.hasUserProfiles ? (
        <div className="pt-4 border-t border-border mt-4">
          <p className="text-xs font-medium text-muted-foreground mb-1">
            User profiles ({folderUsage.usersPath || 'C:\\Users'})
          </p>
          <p className="text-xs text-muted-foreground mb-3">
            Largest profile folders
            {folderUsage.usersTotalGb != null
              ? ` (${folderUsage.usersTotalGb} GB total under Users)`
              : ''}
            . Useful for spotting old accounts left behind.
          </p>
          <FolderUsageList
            folders={folderUsage.userProfiles}
            percentKey="percent_of_used"
            emptyMessage="No user profile sizes available yet."
          />
        </div>
      ) : folderUsage?.hasFolders ? (
        <div className="pt-4 border-t border-border mt-4">
          <p className="text-xs font-medium text-muted-foreground mb-1">User profiles</p>
          <p className="text-sm text-muted-foreground">
            No measurable profiles under Users in this snapshot. Re-run disk-space v1.2.1+ if this
            device still has profiles on the system drive.
          </p>
        </div>
      ) : null}
    </CollapsePanel>
  )
}
